import React, { useRef, useState } from "react";
import { Autocomplete, CloseButton } from "@mantine/core";
import {
  formatOccupationLabel,
  resolveOccupationValue,
  toOccupationOptions,
} from "../helpers/occupation";

const OccupationSelect = ({
  value,
  onChange,
  onBlur,
  onFocus,
  data,
  label,
  placeholder,
  required,
  withAsterisk,
  error,
  disabled,
  readOnly,
  readonly,
  name,
  style,
}) => {
  const options = toOccupationOptions(data);
  const [draft, setDraft] = useState(null);
  const draftRef = useRef(null);
  const pickedRef = useRef(null);
  const queryBaselineRef = useRef(null);
  const locked = Boolean(disabled || readOnly || readonly);
  const committedLabel = formatOccupationLabel(value, options);
  const display = draft ?? committedLabel;

  const suggestions = options.map((item) => ({
    value: String(item.label).trim(),
    occupation: item.value,
  }));

  const rememberDraft = (next) => {
    draftRef.current = next;
    setDraft(next);
  };

  const commit = (text, { trim }) => {
    onChange?.(resolveOccupationValue(text, options, { trim }));
  };

  return (
    <Autocomplete
      name={name}
      style={style}
      label={label}
      placeholder={placeholder}
      required={required}
      withAsterisk={withAsterisk}
      error={error}
      disabled={disabled}
      readOnly={locked}
      data={suggestions}
      value={display}
      limit={suggestions.length}
      maxDropdownHeight={280}
      withinPortal
      zIndex={1000}
      filter={(query, item) => {
        const text = String(query ?? "");
        const baseline = queryBaselineRef.current;
        if (!text.trim() || (baseline != null && text === baseline)) return true;
        return item.value.toLowerCase().trim().includes(text.toLowerCase().trim());
      }}
      onChange={(text) => {
        pickedRef.current = null;
        rememberDraft(text);
        commit(text, { trim: false });
      }}
      onItemSubmit={(item) => {
        pickedRef.current = item.occupation;
        rememberDraft(null);
        onChange?.(item.occupation);
      }}
      onFocus={(event) => {
        if (draftRef.current == null) {
          rememberDraft(committedLabel);
        }
        if (queryBaselineRef.current == null) {
          queryBaselineRef.current = draftRef.current;
        }
        onFocus?.(event);
      }}
      onBlur={(event) => {
        queryBaselineRef.current = null;
        if (pickedRef.current != null) {
          const picked = pickedRef.current;
          pickedRef.current = null;
          rememberDraft(null);
          onChange?.(picked);
          onBlur?.(event);
          return;
        }
        const text = draftRef.current ?? committedLabel;
        commit(text, { trim: true });
        rememberDraft(null);
        onBlur?.(event);
      }}
      rightSection={
        !locked && String(display ?? "").trim() ? (
          <CloseButton
            aria-label="Clear"
            size="sm"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              pickedRef.current = null;
              rememberDraft(null);
              onChange?.("");
            }}
          />
        ) : null
      }
      rightSectionWidth={28}
      styles={{ rightSection: { pointerEvents: "auto" } }}
    />
  );
};

export default OccupationSelect;
